import { useEffect } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { getRoutedTranslatableResourceSections } from '../config/translatableResourceSections';

// Readable page names for analytics. Deliberately not translated, so the reports stay
// comparable across UI languages. Checked top to bottom, the first match wins, so a more
// specific path has to come before its parent
const PAGE_TITLES = [
  { path: '/', exact: true, title: 'Dashboard' },
  { path: '/theme', title: 'Theme' },
  { path: '/static_theme', title: 'Static Theme' },
  { path: '/products', title: 'Products' },
  { path: '/collections', title: 'Collections' },
  { path: '/pages', title: 'Pages' },
  { path: '/navigation', title: 'Navigation' },
  { path: '/blogs', title: 'Blogs' },
  { path: '/articles', title: 'Articles' },
  { path: '/images', title: 'Images' },
  { path: '/shop_metafields', title: 'Shop Metafields' },
  { path: '/shop', title: 'Shop' },
  { path: '/custom', title: 'Custom Content' },
  { path: '/sms_notifications', title: 'SMS Notifications' },
  { path: '/notifications', title: 'Notifications' },
  { path: '/static', title: 'Static Content' },
  { path: '/about', title: 'About' },
  { path: '/login', title: 'Login' },
  { path: '/design', title: 'Design' },
  { path: '/patchnotes', title: 'Changelog' },
  { path: '/changelog', title: 'Changelog' },
  { path: '/settings', title: 'Settings' },
  { path: '/machinetranslation/bulk', title: 'Bulk Translation Settings' },
  { path: '/machinetranslation/wordslog', title: 'Machine Translation Words Log' },
  { path: '/machinetranslation/glossary', title: 'Machine Translation Glossary' },
  { path: '/machinetranslation/settings', title: 'Bulk Translation Configuration' },
  { path: '/machinetranslation', title: 'Machine Translation' },
  { path: '/hidden_admin', title: 'Admin' },
  { path: '/profile/plans', title: 'Plans' },
  { path: '/profile/charges', title: 'Charges' },
  { path: '/profile/tickets', title: 'Support Tickets' },
  { path: '/profile', title: 'Profile' },
  { path: '/metaobjects', title: 'Metaobjects' },
  { path: '/appembeds', title: 'App Embeds' },
  { path: '/appintegrations', title: 'App Integrations' },
  { path: '/apps/size_chart_avada', title: 'Size Chart App' },
  { path: '/apps', title: 'App Integration' },
  { path: '/recommended_apps', title: 'Recommended Apps' },
];

// "selling_plans" -> "Selling Plans"
const humanize = key => key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

const getPageTitle = (pathname) => {
  const routes = [
    ...getRoutedTranslatableResourceSections().map(section => ({ path: `/${section.key}`, title: humanize(section.key) })),
    ...PAGE_TITLES,
  ];
  const route = routes.find(({ path, exact }) => matchPath(pathname, { path, exact }));
  return route ? route.title : pathname;
};

const sendPageView = (pathname) => {
  window.gtag('event', 'page_view', {
    // Without the query string: the embedded app URL carries shop, host and hmac parameters
    page_location: window.location.origin + pathname,
    page_path: pathname,
    page_title: getPageTitle(pathname),
  });
};

// Reports every route change to Google Analytics. gtag and loadGA4() live in index.html; GA4 is
// only loaded once consent has been given. Until then nothing is sent, and the page shown at that
// moment is reported as soon as the 'ga4loaded' event fires
const PageViewTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (!window.ga4Loaded || typeof window.gtag !== 'function') return;
    sendPageView(location.pathname);
  }, [location]);

  useEffect(() => {
    const handleLoaded = () => sendPageView(window.location.pathname);
    window.addEventListener('ga4loaded', handleLoaded);
    return () => window.removeEventListener('ga4loaded', handleLoaded);
  }, []);

  return null;
};

export default PageViewTracker;
